import { useCallback, useEffect, useState } from 'react';
import { formatearCOP } from '@tanteo/compartido';
import {
  alCambiarCola,
  descartarRechazada,
  drenar,
  listarRechazadas,
  type EstadoCola,
  type VentaRechazada,
} from '../lib/cola-offline';
import { useVersionNueva } from '../lib/version-nueva';
import { Boton, BotonEnlace } from './base';
import { Modal } from './Modal';

/**
 * Aviso persistente de la cola: las ventas sin sincronizar y las que el servidor rechazó. Una
 * rechazada se muestra con su motivo y solo sale del equipo cuando el cajero la descarta.
 */
export function AvisoCola() {
  const [cola, setCola] = useState<EstadoCola>({ pendientes: 0, rechazadas: 0 });
  const [enLinea, setEnLinea] = useState(navigator.onLine);
  const [viendo, setViendo] = useState(false);
  const [rechazadas, setRechazadas] = useState<VentaRechazada[]>([]);
  const hayVersionNueva = useVersionNueva();

  useEffect(() => {
    const parar = alCambiarCola(setCola);
    const on = () => setEnLinea(true);
    const off = () => setEnLinea(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      parar();
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const abrir = useCallback(async () => {
    setRechazadas(await listarRechazadas());
    setViendo(true);
  }, []);

  const descartar = useCallback(async (uuid: string) => {
    await descartarRechazada(uuid);
    setRechazadas(await listarRechazadas());
  }, []);

  if (cola.pendientes === 0 && cola.rechazadas === 0 && enLinea && !hayVersionNueva) return null;

  const totalDe = (v: VentaRechazada) =>
    v.lineas.reduce((suma, l) => suma + Math.round(l.cantidad * l.precioUnit), 0);

  return (
    <>
      {/* En columna: la píldora de la cola crece a varias líneas en un teléfono, así que dos avisos
          no caben en la misma fila. La fila de la cola queda dentro, igual que estaba. */}
      <div className="fixed bottom-4 left-1/2 z-40 flex -translate-x-1/2 flex-col items-center gap-2">
        {hayVersionNueva && (
          // Fondo opaco: este aviso cae sobre el contenido de la pantalla, y un tono translúcido deja
          // que el texto de debajo se transparente y no se lea.
          <button
            onClick={() => window.location.reload()}
            className="bg-card text-info ring-info/40 rounded-full px-4 py-2 text-contenido font-bold shadow-lg ring-1"
            data-e2e="aviso-version"
          >
            Hay una versión nueva — tocar para recargar
          </button>
        )}
        <div className="flex gap-2">
          {cola.rechazadas > 0 && (
            <button
              onClick={() => void abrir()}
              className="bg-destructive/15 text-destructive rounded-full px-4 py-2 text-contenido font-bold shadow-lg"
              data-e2e="cola-rechazadas"
            >
              {cola.rechazadas} venta{cola.rechazadas === 1 ? '' : 's'} sin registrar — revisar
            </button>
          )}
          {(cola.pendientes > 0 || !enLinea) && (
            <button
              onClick={() => void drenar()}
              className={`rounded-full px-4 py-2 text-contenido font-bold shadow-lg ${enLinea ? 'bg-warning/15 text-warning' : 'bg-info/15 text-info'}`}
              data-e2e="cola-pendientes"
            >
              {!enLinea && 'Sin conexión — '}
              {cola.pendientes > 0
                ? `${cola.pendientes} venta${cola.pendientes === 1 ? '' : 's'} pendiente${cola.pendientes === 1 ? '' : 's'} de sincronizar`
                : 'las ventas se guardan y se envían al volver la conexión'}
            </button>
          )}
        </div>
      </div>

      <Modal abierto={viendo} titulo="Ventas que el servidor no aceptó" onCerrar={() => setViendo(false)}>
        <p className="text-muted mb-3 text-contenido">
          Estas ventas quedaron guardadas en este equipo porque el servidor las rechazó. Ninguna se
          borra sola: anótalas o vuelve a capturarlas en el mostrador, y luego descártalas de aquí.
        </p>
        {rechazadas.length === 0 ? (
          <p className="text-muted text-contenido">Ya no queda ninguna.</p>
        ) : (
          <ul className="space-y-3">
            {rechazadas.map((v) => (
              <li key={v.uuidCliente} className="border-border rounded-control border p-3" data-e2e="rechazada">
                <p className="num font-extrabold">{formatearCOP(totalDe(v))}</p>
                <p className="text-muted text-etiqueta">
                  {v.fecha} {v.hora} · {v.lineas.length} línea{v.lineas.length === 1 ? '' : 's'}
                </p>
                <p className="text-destructive mt-1 text-contenido font-semibold" data-e2e="rechazada-motivo">{v.motivo}</p>
                <ul className="text-muted mt-1 text-etiqueta">
                  {v.lineas.map((l) => (
                    <li key={l.sku}>
                      {l.cantidad} × {l.nombre}
                    </li>
                  ))}
                </ul>
                <div className="mt-2 flex justify-end">
                  <BotonEnlace tono="peligro" onClick={() => void descartar(v.uuidCliente)} data-e2e="descartar-rechazada">
                    Ya la anoté, descartar
                  </BotonEnlace>
                </div>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-4 flex justify-end">
          <Boton variante="contorno" onClick={() => setViendo(false)}>
            Cerrar
          </Boton>
        </div>
      </Modal>
    </>
  );
}
